/* eslint-disable no-alert */
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody,
  CardHeader, InputGroup,
} from 'reactstrap';
import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { Formik, ErrorMessage } from 'formik';
import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import { post } from '../../helpers/api_helper';
import * as url from '../../helpers/url_helper';

function Pricing(props) {
  const history = useHistory();
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const [signal, setSignal] = useState(0);

  const editListing = (ent) => {
    history.push(`/listings/edit/${ent.id}`);
  };

  const deleteListing = async (listing, e) => {
    const btn = e.currentTarget;

    btn.disabled = true;
    btn.blur();
    btn.firstChild.classList.toggle('bx-spin');

    // eslint-disable-next-line no-restricted-globals
    const cnfrm = confirm(props.t(`Are you sure you want to delete ${listing.sku}?`));

    if (cnfrm) {
      try {
        await post(url.DELETE_LISTING, { id: listing.id });

        toastr.success(`Deleted listing ID: ${listing.id}`, 'Success');
        setSignal(signal + 1);
      } catch (error) {
        toastr.error(error.message, 'Error');
      } finally {
        btn.disabled = false;
        btn.firstChild.classList.toggle('bx-spin');
      }
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="5">
            <Card>
              <CardHeader className="bg-transparent border-bottom">
                {props.t('Adjust listing pricing')}
              </CardHeader>

              <CardBody>
                <Can i="see" this="Listings">
                  <Formik
                    // validate at least one sku, and if % is 100, then use exact price adjustment.
                    initialValues={{
                      price_adjustment: 100,
                      skus: '',
                    }}
                    validate={(values) => {
                      const errors = {};
                      if (!values.skus) {
                        errors.skus = props.t('Please enter at least one SKU');
                      }
                      if (!values.price_adjustment && !values.price_adjustment_2) {
                        errors.price_adjustment = props.t('Please enter a price adjustment');
                      }
                      return errors;
                    }}
                    onSubmit={async (values, { setFieldValue }) => {
                      // submit async
                      // convert skus to array using new line or comma as separator, and trim
                      const skus = values.skus.split(/[\n,]+/).map((sku) => sku.trim());

                      let adjustment = values.price_adjustment;
                      let type = 'percentage';

                      // pick a price option between percentage or exact
                      // if percentage is 100, then use exact price adjustment
                      if (values.price_adjustment === 100 && values.price_adjustment_2) {
                        adjustment = values.price_adjustment_2;
                        type = 'exact';
                      }

                      try {
                        await post(`/api/listings/adjust-pricing/${type}`, {
                          skus,
                          adjustment,
                        });

                        toastr.success('Price adjustment applied', 'Success');

                        // reset form
                        setFieldValue('skus', '');
                        setFieldValue('price_adjustment', 100);
                        setFieldValue('price_adjustment_2', '');
                      } catch (error) {
                        toastr.error(error.message, 'Error');
                      }
                    }}
                  >
                    {({
                      values, handleChange, handleSubmit,
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                          <label htmlFor="skus" className="form-label">{props.t('SKUs (one per line or comma separated)')}</label>
                          <textarea className="form-control" id="skus" name="skus" rows="5" value={values.skus} onChange={handleChange} />
                          <ErrorMessage name="skus" component="div" className="text-danger" />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="price_adjustment" className="form-label">{props.t('Price Adjustment (%)')}</label>
                          <input type="number" step="1" className="form-control" id="price_adjustment" name="price_adjustment" value={values.price_adjustment} onChange={handleChange} />
                          <ErrorMessage name="price_adjustment" component="div" className="text-danger" />
                        </div>

                        <div className="mb-3">
                          <label htmlFor="price_adjustment_2" className="form-label">{props.t('Price Adjustment (exact)')}</label>
                          <InputGroup>
                            <span className="input-group-text">$</span>
                            <input type="number" step="0.1" min="5" className="form-control" id="price_adjustment_2" name="price_adjustment_2" value={values.price_adjustment_2} onChange={handleChange} />
                          </InputGroup>
                        </div>
                        <button type="submit" className="btn btn-primary">{props.t('Apply Adjustment')}</button>
                      </form>
                    )}
                  </Formik>
                </Can>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xxl="10">
            <Card>
              <CardHeader className="bg-transparent border-bottom">
                {props.t('Listings with highest ISU')}
              </CardHeader>
              <CardBody>
                <Can i="see" this="Listings">
                  <BaseGrid
                    url="/api/listings/fastest"
                    hasSearch
                    shouldUpdate={signal}
                    className="table table-sm table-hover table-bordered fs-6"
                    config={
                    {
                      columns: {
                        thumb: {
                          label: '',
                          image: {
                            src: (listing) => (`/api/listings/thumbnail/${listing.id}`),
                            class: 'rounded',
                            style: { maxHeight: '6rem', maxWidth: '6rem' },
                          },
                          translate: false,
                        },
                        id: {
                          label: 'ID',
                          style: { minWidth: '100px' },
                          link: {
                            click: (ent) => {
                              window.open(`/listing/${ent.id}`, '_blank');
                            },
                          },
                          html: true,
                          format: (x, lst) => {
                            if (lst.last_qty > 0) {
                              // In Stock
                              return `<span class="badge bg-success text-dark"><span class="mdi mdi-check"></span></span> &nbsp;${x}`;
                            }

                            // OOS
                            return `<span class="badge bg-warning bg-soft text-dark"><span class="mdi mdi-close-thick text-dark"></span></span> &nbsp;${x}`;
                          },
                        },
                        latest_thirty_rate: {
                          label: 'ISU',
                          style: { fontSize: '13px' },
                          html: true,
                          format: (x) => `<strong>${x.toFixed(1)}</strong>`,
                        },
                        used_qty: {
                          label: 'Uses',
                          style: { fontSize: '13px' },
                        },
                        price: {
                          label: 'Price',
                          format: (x) => new Intl.NumberFormat(
                            'en-US',
                            {
                              style: 'currency',
                              currency: 'USD',
                            },
                          ).format(x),
                          style: { fontSize: '13px' },
                        },
                        last_qty: {
                          label: 'Qty for Sale',
                          style: { fontSize: '13px' },
                        },
                        sku: {
                          label: 'Sku',
                        },
                        title: { label: 'Title', style: { fontSize: '11px', maxWidth: '200px' } },
                        condition: { label: 'Condition', style: { fontSize: '11px' } },
                        created_at: {
                          label: props.t('Created'),
                          date: {
                            format: (date) => (moment(date).fromNow()),
                            timezone: process.env.LOCAL_TZ,
                          },
                          style: { fontSize: '11px' },
                        },
                        actions: {
                          label: props.t('Actions'),
                          actions: [
                            {
                              key: 'edit',
                              icon: 'bx bx-pencil',
                              class: 'btn btn-sm btn-soft-dark',
                              alt: props.t('Edit'),
                              click: editListing,
                            },
                            {
                              key: 'delete',
                              icon: 'mdi mdi-delete',
                              class: 'btn btn-sm btn-soft-danger',
                              alt: props.t('Delete'),
                              click: deleteListing,
                            },
                          ],
                        },
                      },
                      pagination: {
                        sort: 'id',
                        direction: 'desc',
                        perPage: 30,
                      },
                    }
                    }
                  />
                </Can>

                <Can not i="see" this="Listings"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

Pricing.defaultProps = {
  t: (x) => (x),
};

Pricing.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Pricing));
